import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  ArrowRight,
  BarChart3,
  Building2,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Flame,
  History,
  MessageSquareText,
  PhoneCall,
  Target,
  Trophy,
  UserRound,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api, useAuth } from "./auth";
type Row = Record<string, any>;
const stages = [
  "NEW",
  "ATTEMPTING",
  "CONTACTED",
  "QUALIFIED",
  "VIEWING",
  "PROPOSAL",
  "NEGOTIATION",
  "WON",
  "DEAL_CLOSED",
];
const label = (value: unknown) =>
  String(value || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (x) => x.toUpperCase());
const money = (value: unknown, currency = "AED") =>
  new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
const split = (value: FormDataEntryValue | null) =>
  String(value || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
const nextQuarterHourTomorrow = () => {
  const value = new Date();
  value.setDate(value.getDate() + 1);
  value.setSeconds(0, 0);
  value.setMinutes(Math.ceil(value.getMinutes() / 15) * 15);
  const offset = value.getTimezoneOffset() * 60_000;
  return new Date(value.getTime() - offset).toISOString().slice(0, 16);
};

export function WorkingListWorkspace() {
  const { csrfToken } = useAuth();
  const [leads, setLeads] = useState<Row[]>([]),
    [tasks, setTasks] = useState<Row[]>([]),
    [summary, setSummary] = useState<Row>({ stages: {} }),
    [outcomes, setOutcomes] = useState<Row[]>([]),
    [task, setTask] = useState<Row | null>(null),
    [lead, setLead] = useState<Row | null>(null),
    [leadBundle, setLeadBundle] = useState<Row | null>(null),
    [propertyBundle, setPropertyBundle] = useState<Row>({ data: [], available: [] }),
    [mode, setMode] = useState<"DETAIL" | "CALL" | "PROGRESS">("DETAIL"),
    [transitionTarget, setTransitionTarget] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  async function load() {
    try {
      const [l, t, s, o] = await Promise.all([
        api<{ data: Row[] }>("/api/v1/enterprise/working-list"),
        api<{ data: Row[] }>("/api/v1/enterprise/tasks"),
        api<{ data: Row }>("/api/v1/enterprise/working-list/summary"),
        api<{ data: Row[] }>("/api/v1/enterprise/working-list/outcomes"),
      ]);
      setLeads(l.data);
      setTasks(
        t.data.filter((x) => !["COMPLETED", "CANCELLED"].includes(x.status)),
      );
      setSummary(s.data);
      setOutcomes(o.data);
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : "Working list could not be loaded",
      );
    }
  }
  useEffect(() => {
    void load();
  }, []);
  const overdue = useMemo(
    () =>
      tasks.filter((x) => x.due_at && Date.parse(x.due_at) < Date.now()).length,
    [tasks],
  );
  async function openLead(row: Row) {
    setLead(row);
    setLeadBundle(null);
    setPropertyBundle({ data: [], available: [] });
    setMode("DETAIL");
    setTransitionTarget("");
    setError("");
    try {
      const [bundle, properties] = await Promise.all([
        api<Row>(`/api/v1/enterprise/lead-profile/${row.id}`),
        api<Row>(`/api/v1/enterprise/working-list/${row.id}/properties`),
      ]);
      setLeadBundle(bundle);
      setPropertyBundle(properties);
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Lead could not be opened",
      );
    }
  }
  async function linkProperties(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken || !lead) return;
    const form = new FormData(event.currentTarget), propertyIds = form.getAll("propertyIds").map(String);
    setBusy(true);
    try {
      await api(`/api/v1/enterprise/working-list/${lead.id}/properties`, csrfToken, { method: "POST", body: JSON.stringify({ propertyIds, comment: form.get("comment") }) });
      event.currentTarget.reset();
      await openLead(lead);
    } catch (value) { setError(value instanceof Error ? value.message : "Properties could not be linked"); }
    finally { setBusy(false); }
  }
  async function updatePropertyMatch(event: FormEvent<HTMLFormElement>, propertyId: string) {
    event.preventDefault();
    if (!csrfToken || !lead) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    try {
      await api(`/api/v1/enterprise/working-list/${lead.id}/properties/${propertyId}`, csrfToken, { method: "PATCH", body: JSON.stringify({ status: d.status, comment: d.comment, viewingAt: d.viewingAt ? new Date(String(d.viewingAt)).toISOString() : null, customerFeedback: d.customerFeedback || null, outcome: d.outcome || null }) });
      await openLead(lead);
    } catch (value) { setError(value instanceof Error ? value.message : "Property engagement could not be updated"); }
    finally { setBusy(false); }
  }
  async function updateTask(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken || !task) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    await api(`/api/v1/enterprise/tasks/${task.id}`, csrfToken, {
      method: "PATCH",
      body: JSON.stringify({
        status: d.status,
        progress: Number(d.progress),
        comment: d.comment,
      }),
    });
    setTask(null);
    await load();
  }
  async function progress(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken || !lead) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    try {
      await api(
        `/api/v1/enterprise/working-list/${lead.id}/progress`,
        csrfToken,
        {
          method: "POST",
          body: JSON.stringify({
            targetStage: d.targetStage,
            comment: d.comment,
            outcome: d.outcome || undefined,
            temperature: d.temperature || undefined,
            lostReason: d.lostReason || null,
            propertyId: d.propertyId || null,
            nextActionAt: d.nextActionAt
              ? new Date(String(d.nextActionAt)).toISOString()
              : null,
          }),
        },
      );
      await load();
      if (["WON", "LOST", "DEAL_CLOSED"].includes(String(d.targetStage))) {
        setLead(null);
        setLeadBundle(null);
      } else await openLead(leads.find((x) => x.id === lead.id) || lead);
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Lead could not be progressed",
      );
    } finally {
      setBusy(false);
    }
  }
  async function callUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken || !lead) return;
    const f = new FormData(event.currentTarget),
      d = Object.fromEntries(f);
    setBusy(true);
    try {
      await api(
        `/api/v1/enterprise/working-list/${lead.id}/call-update`,
        csrfToken,
        {
          method: "POST",
          body: JSON.stringify({
            outcome: d.outcome,
            comment: d.comment,
            nextActionAt: d.nextActionAt
              ? new Date(String(d.nextActionAt)).toISOString()
              : null,
            temperature: d.temperature || undefined,
            preferredContactChannel: d.preferredContactChannel || null,
            preferredLanguage: d.preferredLanguage || null,
            contactTimePreference: d.contactTimePreference || null,
            buyingTimeframe: d.buyingTimeframe || null,
            decisionAuthority: d.decisionAuthority || null,
            motivation: d.motivation || null,
            interests: split(f.get("interests")),
            preferredAreas: split(f.get("preferredAreas")),
            bedrooms: d.bedrooms ? Number(d.bedrooms) : null,
            budgetMin: d.budgetMin ? Number(d.budgetMin) : null,
            budgetMax: d.budgetMax ? Number(d.budgetMax) : null,
          }),
        },
      );
      await load();
      await openLead(leads.find((x) => x.id === lead.id) || lead);
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : "Call update could not be saved",
      );
    } finally {
      setBusy(false);
    }
  }
  const currentStage = String(leadBundle?.data?.stage || lead?.stage || "NEW"),
    nextStage = stages[stages.indexOf(currentStage) + 1];
  const transitionOptions =
    currentStage === "WON"
      ? ["DEAL_CLOSED"]
      : [
          ...stages
            .slice(Math.max(0, stages.indexOf(currentStage) + 1))
            .filter((x) => x !== "DEAL_CLOSED"),
          "LOST",
        ];
  function chooseTransition(target: string) {
    setTransitionTarget(target);
    setMode("PROGRESS");
  }
  return (
    <div className="content working-list-page candidate18-working">
      <section className="page-heading working-hero">
        <div>
          <span className="kicker">My live execution queue</span>
          <h1>Working list</h1>
          <p>
            Work every claimed customer, protect the 24-hour clock and move
            opportunities forward with a recorded update.
          </p>
        </div>
        <Link className="button primary" to="/modules/crm">
          <Target />
          Open lead pool
        </Link>
      </section>
      {error && <div className="form-error">{error}</div>}
      <section className="working-summary">
        <article>
          <Target />
          <div>
            <span>Active leads</span>
            <strong>{summary.total || 0}</strong>
          </div>
        </article>
        <article className={Number(summary.expiring_soon) ? "danger" : ""}>
          <Clock3 />
          <div>
            <span>Expiring ≤4h</span>
            <strong>{summary.expiring_soon || 0}</strong>
          </div>
        </article>
        <article>
          <PhoneCall />
          <div>
            <span>Not contacted</span>
            <strong>{summary.not_contacted || 0}</strong>
          </div>
        </article>
        <article>
          <CalendarClock />
          <div>
            <span>Overdue actions</span>
            <strong>{summary.overdue_actions || 0}</strong>
          </div>
        </article>
        <article>
          <CheckCircle2 />
          <div>
            <span>Open tasks</span>
            <strong>{tasks.length}</strong>
            <small>{overdue} overdue</small>
          </div>
        </article>
        <article className="success">
          <Trophy />
          <div><span>Won / closed</span><strong>{summary.won || 0}</strong><small>{summary.deal_closed || 0} commission-ready</small></div>
        </article>
      </section>
      <section className="panel phase-command">
        <header>
          <div>
            <span className="kicker">My phase distribution</span>
            <h2>Pipeline movement</h2>
          </div>
          <small>Every forward move requires a comment</small>
        </header>
        <div className="phase-strip">
          {stages.map((stage, index) => (
            <div key={stage}>
              <span>{label(stage)}</span>
              <strong>{summary.stages?.[stage] || 0}</strong>
              {index < stages.length - 1 && <ChevronRight />}
            </div>
          ))}
        </div>
      </section>
      <section className="panel working-analytics">
        <header><div><span className="kicker">My conversion command centre</span><h2>Live funnel and outcomes</h2></div><BarChart3/></header>
        <div className="working-chart">
          {stages.map((stage) => { const count = stage === "WON" ? Number(summary.won || 0) : stage === "DEAL_CLOSED" ? Number(summary.deal_closed || 0) : Number(summary.stages?.[stage] || 0); const max = Math.max(1, Number(summary.total || 0), Number(summary.won || 0)); return <article key={stage}><div><span>{label(stage)}</span><strong>{count}</strong></div><i><b style={{ width: `${Math.max(count ? 8 : 0, count / max * 100)}%` }}/></i></article>; })}
        </div>
      </section>
      <section className="working-board">
        <article className="panel lead-queue">
          <header>
            <div>
              <span className="kicker">Revenue actions</span>
              <h2>Customers I am working</h2>
            </div>
            <b>{leads.length}</b>
          </header>
          <div className="compact-lead-list">
            {leads.map((row) => {
              const hours = Math.max(
                0,
                Math.ceil(
                  (Date.parse(row.auto_return_at) - Date.now()) / 3600000,
                ),
              );
              return (
                <button
                  className={hours <= 4 ? "at-risk" : ""}
                  key={row.assignment_id}
                  onClick={() => void openLead(row)}
                >
                  <span className="lead-avatar">
                    {String(row.display_name || "?")
                      .split(/\s+/)
                      .map((x: string) => x[0])
                      .slice(0, 2)
                      .join("")}
                  </span>
                  <div>
                    <strong>{row.display_name}</strong>
                    <p>
                      {row.reference} ·{" "}
                      {label(row.lead_category || row.business_module)}
                    </p>
                    <small>
                      {label(row.stage)} · next{" "}
                      {row.next_action_at
                        ? new Date(row.next_action_at).toLocaleString("en-AE")
                        : "action not set"}
                    </small>
                  </div>
                  <span className="temperature">
                    <Flame />
                    {label(row.temperature)}
                  </span>
                  <span className="claim-clock">
                    <strong>{hours}h</strong>
                    <small>remaining</small>
                  </span>
                  <ChevronRight />
                </button>
              );
            })}
            {!leads.length && (
              <div className="empty">
                No claimed customers. Open the lead pool to claim work.
              </div>
            )}
          </div>
        </article>
        <article className="panel task-queue">
          <header>
            <div>
              <span className="kicker">Delivery actions</span>
              <h2>Tasks requiring progress</h2>
            </div>
            <b>{tasks.length}</b>
          </header>
          <div className="working-stack">
            {tasks.map((row) => (
              <button
                className={
                  row.due_at && Date.parse(row.due_at) < Date.now()
                    ? "at-risk"
                    : ""
                }
                key={row.id}
                onClick={() => setTask(row)}
              >
                <div className="working-icon">
                  <CheckCircle2 />
                </div>
                <div>
                  <strong>{row.title}</strong>
                  <p>
                    {label(row.status)} · {row.progress || 0}% ·{" "}
                    {label(row.priority)}
                  </p>
                  <small>
                    {row.due_at
                      ? `Due ${new Date(row.due_at).toLocaleString("en-AE")}`
                      : "No deadline"}{" "}
                    · {row.comment_count || 0} updates
                  </small>
                </div>
                <span className="mini-progress">
                  <i style={{ width: `${row.progress || 0}%` }} />
                </span>
              </button>
            ))}
          </div>
        </article>
      </section>
      <section className="panel won-history">
        <header><div><span className="kicker">Retained relationship history</span><h2>My won, closed and lost opportunities</h2></div><b>{outcomes.length}</b></header>
        <div className="outcome-grid">{outcomes.map((row) => <button key={row.id} onClick={() => void openLead(row)}><span className={`outcome-badge ${String(row.stage).toLowerCase()}`}>{label(row.stage)}</span><strong>{row.display_name}</strong><small>{row.reference} · {label(row.transaction_type || row.business_module)}</small><p>{(row.properties || []).map((x: Row) => x.title).join(", ") || "No property linked"}</p>{row.work_locked_until && Date.parse(row.work_locked_until) > Date.now() && <em>Cooling period ends {new Date(row.work_locked_until).toLocaleString("en-AE")}</em>}</button>)}</div>
        {!outcomes.length && <div className="empty">Won and lost opportunities will remain here with their complete history.</div>}
      </section>
      {lead && (
        <div
          className="drawer-backdrop"
          onMouseDown={(e) => e.target === e.currentTarget && setLead(null)}
        >
          <aside className="record-drawer working-lead-drawer">
            <header>
              <div>
                <span className="kicker">
                  {lead.reference} · {label(currentStage)}
                </span>
                <h2>{lead.display_name}</h2>
                <p>
                  {lead.mobile || lead.email || "Contact details protected"}
                </p>
              </div>
              <button className="icon-button" onClick={() => setLead(null)}>
                <X />
              </button>
            </header>
            <div className="lead-journey" aria-label="Lead journey">
              {stages.map((stage, index) => {
                const currentIndex = stages.indexOf(currentStage), complete = index < currentIndex, active = index === currentIndex;
                return <div className={complete ? "complete" : active ? "active" : ""} key={stage}><i>{complete ? <CheckCircle2/> : index + 1}</i><span>{label(stage)}</span></div>;
              })}
            </div>
            <div className="lead-action-bar">
              <button
                className="button primary"
                onClick={() => setMode("CALL")}
              >
                <PhoneCall />
                Call customer
              </button>
              {nextStage && (
                <button
                  className="button secondary"
                  onClick={() => chooseTransition(nextStage)}
                >
                  <ArrowRight />
                  {currentStage === "WON"
                    ? "Close deal"
                    : `Advance to ${label(nextStage)}`}
                </button>
              )}
              {currentStage !== "WON" && (
                <>
                  <button
                    className="button success"
                    onClick={() => chooseTransition("WON")}
                  >
                    <CheckCircle2 />
                    Mark won
                  </button>
                  <button
                    className="button danger"
                    onClick={() => chooseTransition("LOST")}
                  >
                    Mark lost
                  </button>
                </>
              )}
              <button
                className="button secondary"
                onClick={() => setMode("DETAIL")}
              >
                <UserRound />
                Customer summary
              </button>
            </div>
            {mode === "DETAIL" && (
              <><LeadSummary bundle={leadBundle} lead={lead} /><PropertyEngagement bundle={propertyBundle} busy={busy} onLink={linkProperties} onUpdate={updatePropertyMatch}/></>
            )}{" "}
            {mode === "PROGRESS" && (
              <form className="guided-work-form" onSubmit={progress}>
                <header>
                  <div>
                    <span className="kicker">
                      Controlled pipeline progression
                    </span>
                    <h3>
                      {label(currentStage)} <ArrowRight />{" "}
                      {label(transitionTarget || nextStage)}
                    </h3>
                  </div>
                </header>
                <div className="form-grid">
                  <label>
                    Move to stage
                    <select
                      name="targetStage"
                      required
                      value={transitionTarget || nextStage || ""}
                      onChange={(event) =>
                        setTransitionTarget(event.target.value)
                      }
                    >
                      {transitionOptions.map((stage) => (
                        <option key={stage} value={stage}>
                          {label(stage)}
                        </option>
                      ))}
                    </select>
                    <small>
                      Choose the next phase or skip forward. Won and Lost stop
                      the work clock.
                    </small>
                  </label>
                  <label>
                    Outcome
                    <select name="outcome" required>
                      <option value="">Choose the result…</option>
                      <option>Positive progress</option>
                      <option>Customer action pending</option>
                      <option>Internal action pending</option>
                      <option>Meeting or viewing scheduled</option>
                    </select>
                  </label>
                  <label>
                    Temperature
                    <select name="temperature" defaultValue={lead.temperature}>
                      <option>COLD</option>
                      <option>WARM</option>
                      <option>HOT</option>
                    </select>
                  </label>
                  <label>
                    Next action
                    <input
                      name="nextActionAt"
                      type="datetime-local"
                      step={900}
                      defaultValue={nextQuarterHourTomorrow()}
                      disabled={["WON", "LOST", "DEAL_CLOSED"].includes(
                        transitionTarget,
                      )}
                      required={
                        !["WON", "LOST", "DEAL_CLOSED"].includes(
                          transitionTarget,
                        )
                      }
                    />
                  </label>
                  {transitionTarget === "LOST" && (
                    <label className="span-2">
                      Lost reason
                      <textarea
                        name="lostReason"
                        required
                        rows={2}
                        placeholder="Record why the opportunity was lost."
                      />
                    </label>
                  )}
                  {transitionTarget === "DEAL_CLOSED" && (
                    <label className="span-2">Closed property<select name="propertyId" required><option value="">Select the property sold or rented…</option>{((propertyBundle.data || []) as Row[]).map((property) => <option value={property.property_id} key={property.property_id}>{property.reference} · {property.title_en} · {label(property.listing_type || lead.transaction_type)}</option>)}</select><small>Closing the deal marks this property Sold or Rented, withdraws portal listings and prevents further offers for 30 days.</small></label>
                  )}
                  <label className="span-2">
                    Mandatory update comment
                    <textarea
                      name="comment"
                      required
                      minLength={3}
                      rows={4}
                      placeholder="What changed, what was agreed and what happens next?"
                    />
                  </label>
                </div>
                <footer>
                  <button
                    type="button"
                    className="button secondary"
                    onClick={() => setMode("DETAIL")}
                  >
                    Cancel
                  </button>
                  <button className="button primary" disabled={busy}>
                    {transitionTarget === "DEAL_CLOSED"
                      ? "Close deal and enable commission"
                      : `Move to ${label(transitionTarget || nextStage)}`}
                  </button>
                </footer>
              </form>
            )}
            {mode === "CALL" && (
              <GuidedCall
                lead={leadBundle?.data || lead}
                busy={busy}
                onCancel={() => setMode("DETAIL")}
                onSubmit={callUpdate}
              />
            )}
          </aside>
        </div>
      )}
      {task && (
        <div className="modal-backdrop">
          <form className="modal" onSubmit={updateTask}>
            <header>
              <div>
                <span className="kicker">Quick progress update</span>
                <h2>{task.title}</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setTask(null)}
              >
                <X />
              </button>
            </header>
            <div className="form-grid">
              <label>
                Status
                <select name="status" defaultValue={task.status}>
                  <option>OPEN</option>
                  <option>IN_PROGRESS</option>
                  <option>BLOCKED</option>
                  <option>COMPLETED</option>
                </select>
              </label>
              <label>
                Progress
                <input
                  name="progress"
                  type="number"
                  min="0"
                  max="100"
                  defaultValue={task.progress || 0}
                />
              </label>
              <label className="span-2">
                Update comment
                <textarea name="comment" required rows={4} />
              </label>
            </div>
            <footer>
              <button
                type="button"
                className="button secondary"
                onClick={() => setTask(null)}
              >
                Cancel
              </button>
              <button className="button primary">
                <MessageSquareText />
                Save update
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}

function LeadSummary({ bundle, lead }: { bundle: Row | null; lead: Row }) {
  const d = bundle?.data || lead,
    p = bundle?.customerProfile;
  return (
    <div className="lead-summary">
      <section className="lead-facts">
        <div>
          <span>Requirement</span>
          <strong>{d.requirement || "Qualification pending"}</strong>
        </div>
        <div>
          <span>Budget</span>
          <strong>{money(d.budget_max || d.budget_min, d.currency)}</strong>
        </div>
        <div>
          <span>Preferred areas</span>
          <strong>
            {(p?.preferred_areas || []).join(", ") ||
              d.community ||
              d.area ||
              "Not captured"}
          </strong>
        </div>
        <div>
          <span>Buying timeframe</span>
          <strong>{label(p?.buying_timeframe) || "Not captured"}</strong>
        </div>
        <div>
          <span>Decision authority</span>
          <strong>{label(p?.decision_authority) || "Not captured"}</strong>
        </div>
        <div>
          <span>Last activity</span>
          <strong>
            {d.last_activity_at
              ? new Date(d.last_activity_at).toLocaleString("en-AE")
              : "No activity"}
          </strong>
        </div>
      </section>
      <section>
        <h3>
          <History />
          Recent customer history
        </h3>
        <div className="timeline">
          {[...(bundle?.activities || []), ...(bundle?.comments || [])]
            .sort(
              (a, b) =>
                Date.parse(b.occurred_at || b.created_at) -
                Date.parse(a.occurred_at || a.created_at),
            )
            .slice(0, 10)
            .map((item: Row) => (
              <article key={item.id}>
                <i />
                <div>
                  <strong>
                    {label(item.activity_type || "COMMENT")} ·{" "}
                    {item.created_by_name}
                  </strong>
                  <p>{item.outcome || item.body}</p>
                  <small>
                    {new Date(
                      item.occurred_at || item.created_at,
                    ).toLocaleString("en-AE")}
                  </small>
                </div>
              </article>
            ))}
          {!bundle && <div className="empty">Loading customer history…</div>}
        </div>
      </section>
    </div>
  );
}

function PropertyEngagement({ bundle, busy, onLink, onUpdate }: { bundle: Row; busy: boolean; onLink: (event: FormEvent<HTMLFormElement>) => void; onUpdate: (event: FormEvent<HTMLFormElement>, propertyId: string) => void }) {
  const matches = (bundle.data || []) as Row[], available = (bundle.available || []) as Row[];
  const [query,setQuery] = useState("");
  const visible = available.filter((property) => [property.reference,property.title_en,property.unit_number,property.community,property.developer_name,property.listing_type].join(" ").toLowerCase().includes(query.toLowerCase()));
  return <section className="lead-property-engagement">
    <header><div><span className="kicker">Property recommendation history</span><h3><Building2/>Properties / units shown to this customer</h3></div><b>{matches.length}</b></header>
    <form className="property-link-form" onSubmit={onLink}>
      <div className="property-picker"><label>Search available properties<input value={query} onChange={(event)=>setQuery(event.target.value)} placeholder="Reference, project, community, developer…"/></label><div className="property-pick-results">{visible.slice(0,40).map((property)=><label key={property.id}><input type="checkbox" name="propertyIds" value={property.id}/><span><strong>{property.reference} · {property.title_en}</strong><small>{label(property.listing_type)} · {property.community || "Area not recorded"} · {money(property.asking_price, property.currency)}</small></span></label>)}{!visible.length&&<div className="empty">No available property matches your search.</div>}</div></div>
      <label className="property-match-reason">Why these properties?<textarea name="comment" required minLength={3} rows={5} placeholder="Match rationale, customer preference or recommendation context"/></label>
      <button className="button primary property-tag-submit" disabled={busy}>Tag selected properties</button>
    </form>
    <div className="property-engagement-list">{matches.map((match) => <article key={match.property_id}>
      <div className="property-engagement-title"><div><strong>{match.reference} · {match.title_en}</strong><small>{label(match.property_type)} · {match.unit_number ? `Unit ${match.unit_number} · ` : ""}{match.community || "Community not recorded"}</small></div><span className="status">{label(match.status)}</span></div>
      <form onSubmit={(event) => onUpdate(event, String(match.property_id))} className="property-progress-form">
        <label>Engagement stage<select name="status" defaultValue={match.status}>{["SHORTLISTED","PRESENTED","VIEWING_SCHEDULED","VIEWED","INTERESTED","OFFERED","REJECTED","CLOSED"].map((stage) => <option key={stage}>{stage}</option>)}</select></label>
        <label>Viewing time<input name="viewingAt" type="datetime-local" step={900} defaultValue={match.viewing_at ? new Date(new Date(match.viewing_at).getTime() - new Date(match.viewing_at).getTimezoneOffset() * 60000).toISOString().slice(0,16) : ""}/></label>
        <label>Customer feedback<input name="customerFeedback" defaultValue={match.customer_feedback || ""}/></label>
        <label>Outcome<input name="outcome" defaultValue={match.outcome || ""}/></label>
        <label className="span-2">Mandatory update comment<textarea name="comment" required minLength={3} rows={2}/></label>
        <button className="button secondary" disabled={busy}>Save property update</button>
      </form>
      <details><summary>{(match.events || []).length} history event(s)</summary>{(match.events || []).map((event: Row, index: number) => <p key={index}><strong>{label(event.event_type)} · {label(event.new_status)}</strong> — {event.remarks} <small>{event.actor} · {new Date(event.created_at).toLocaleString("en-AE")}</small></p>)}</details>
    </article>)}</div>
    {!matches.length && <div className="empty">No properties have been recommended yet. Tag the shortlist before arranging a viewing.</div>}
  </section>;
}

function GuidedCall({
  lead,
  busy,
  onCancel,
  onSubmit,
}: {
  lead: Row;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form className="guided-work-form call-coach" onSubmit={onSubmit}>
      <header>
        <div>
          <span className="kicker">Guided customer call</span>
          <h3>Qualify with clicks, then record the conversation</h3>
        </div>
      </header>
      <div className="form-grid">
        <label>
          Call outcome
          <select name="outcome" required defaultValue="">
            <option value="" disabled>
              Select outcome…
            </option>
            {[
              "NO_ANSWER",
              "CONNECTED",
              "CALLBACK",
              "MEETING_BOOKED",
              "VIEWING_BOOKED",
              "QUALIFIED",
              "NOT_INTERESTED",
              "WRONG_NUMBER",
              "DO_NOT_CONTACT",
            ].map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <label>
          Lead temperature
          <select name="temperature" defaultValue={lead.temperature}>
            <option>COLD</option>
            <option>WARM</option>
            <option>HOT</option>
          </select>
        </label>
        <label>
          Preferred channel
          <select name="preferredContactChannel">
            <option value="">Not confirmed</option>
            <option>PHONE</option>
            <option>WHATSAPP</option>
            <option>EMAIL</option>
            <option>SMS</option>
          </select>
        </label>
        <label>
          Preferred language
          <select name="preferredLanguage">
            <option value="">Not confirmed</option>
            <option>English</option>
            <option>Arabic</option>
            <option>Hindi</option>
            <option>Urdu</option>
            <option>Russian</option>
            <option>French</option>
            <option>Mandarin</option>
          </select>
        </label>
        <label>
          Best contact time
          <select name="contactTimePreference">
            <option value="">Not confirmed</option>
            <option>Morning 08:00–12:00</option>
            <option>Afternoon 12:00–17:00</option>
            <option>Evening 17:00–21:00</option>
            <option>Weekends</option>
          </select>
        </label>
        <label>
          Buying timeframe
          <select name="buyingTimeframe">
            <option value="">Not confirmed</option>
            <option>IMMEDIATE</option>
            <option>ONE_TO_THREE_MONTHS</option>
            <option>THREE_TO_SIX_MONTHS</option>
            <option>SIX_TO_TWELVE_MONTHS</option>
            <option>EXPLORING</option>
          </select>
        </label>
        <label>
          Decision authority
          <select name="decisionAuthority">
            <option value="">Not confirmed</option>
            <option>SOLE</option>
            <option>JOINT</option>
            <option>ADVISER</option>
            <option>UNKNOWN</option>
          </select>
        </label>
        <label>
          Bedrooms
          <select name="bedrooms">
            <option value="">Any / unknown</option>
            {[0, 1, 2, 3, 4, 5, 6].map((x) => (
              <option key={x} value={x}>
                {x === 0 ? "Studio" : `${x}+`}
              </option>
            ))}
          </select>
        </label>
        <label>
          Minimum budget
          <input name="budgetMin" type="number" min="0" />
        </label>
        <label>
          Maximum budget
          <input name="budgetMax" type="number" min="0" />
        </label>
        <label>
          Preferred areas
          <input
            name="preferredAreas"
            placeholder="Dubai Marina, Downtown Dubai"
          />
        </label>
        <label>
          Interests
          <input name="interests" placeholder="Golf, schools, waterfront" />
        </label>
        <label className="span-2">
          Primary motivation
          <textarea name="motivation" rows={2} />
        </label>
        <label>
          Next action
          <input
            name="nextActionAt"
            type="datetime-local"
            step={900}
            defaultValue={nextQuarterHourTomorrow()}
          />
        </label>
        <label className="span-2">
          Mandatory call notes
          <textarea name="comment" required minLength={3} rows={4} />
        </label>
      </div>
      <footer>
        <button type="button" className="button secondary" onClick={onCancel}>
          Cancel
        </button>
        <button className="button primary" disabled={busy}>
          <PhoneCall />
          Save call update
        </button>
      </footer>
    </form>
  );
}
